import React, { useState, Fragment, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { uploadMatterCD } from "../../action/matterCD";
import { setAlert } from "../../action/alert";
import Alert from "../layout/Alert";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
} from "@material-ui/core";

function UploadCDForm({ activeTab }) {
  const allowedDeviceCategories =
    useSelector((state) => state.auth.allowedDeviceCategories) || [];
  const dispatch = useDispatch();
  const [csvProductIds, setCsvProductIds] = useState([]);
  const [fwVersion, setFwVersion] = useState("");
  const [certificate, setCertificate] = useState("");
  const [category, setCategory] = useState("");
  const [cdFile, setCdFile] = useState(null);
  const [error, setError] = useState("");
  const [fwVersionError, setFwVersionError] = useState("");
  const [loading, setLoading] = useState(false);
  const csvInputRef = useRef(null); 
  const cdInputRef = useRef(null); 
  const resetForm = () => {
    setFwVersion("");
    setCertificate("");
    setCategory("");
    setCsvProductIds([]);
    setCdFile(null);
    setError("");
    setFwVersionError("");
    setLoading(false);
    if (csvInputRef.current) csvInputRef.current.value = "";
    if (cdInputRef.current) cdInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFwVersionError("");
    if (!cdFile) {
      setError("Please select a CD file to upload.");
      return;
    }
    if (!/^\d+\.\d+$/.test(fwVersion)) {
      setFwVersionError(
        "Firmware Version must be a decimal value (e.g. 1.0, 2.5)"
      );
      return;
    }
    setError("");
    setLoading(true);

    const formData = new FormData();
    formData.append("cd", cdFile);
    formData.append("category", category);
    formData.append("fwVersion", fwVersion);
    formData.append("productIds", csvProductIds.join(","));
    try {
      await uploadMatterCD(formData);
      dispatch(setAlert("CDs uploaded successfully!", "success"));
      resetForm();
    } catch (err) {
      let errMsg = err.response?.data?.message || err.message;
      if (
        typeof errMsg === "string" &&
        errMsg.toLowerCase().includes("already exist")
      ) {
        errMsg = "A record with these details already exists.";
      }
      dispatch(setAlert(errMsg, "danger"));
      setError("");
    }
    setLoading(false);
  };
const handleCSVUpload = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const fileExtension = file.name.split('.').pop().toLowerCase();
  if (fileExtension !== 'csv') {
    dispatch(
      setAlert(
        "Invalid file type. Please upload a '.csv' file containing Product IDs.",
        "danger"
      )
    );
    setCsvProductIds([]);
    e.target.value = "";
    return;
  }

  const reader = new FileReader();
  reader.onload = function (event) {
    const text = event.target.result;
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean); 
    const ids = lines.slice(1);

    // Accept all Product IDs - backend will validate based on category
    setCsvProductIds(ids);
  };
  reader.readAsText(file);
};


  return (
    <div className="content-block bottom-shadow" style={{ position: 'relative' }}>
      <Alert />
      <div className="py-15">
        <form onSubmit={handleSubmit} style={{ maxWidth: 700 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Upload CSV */}
            <Grid item xs={4}>
              <label className="title" style={{ marginLeft: 32 }}>
                Upload CSV of ProductIds:
              </label>
            </Grid>
            <Grid item xs={8}>
              <input
              ref={csvInputRef} 
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                style={{ color: "black", marginBottom: 6 }}
              />
              {csvProductIds.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong style={{ color: "black" }}>Total ProductIds:</strong>
                  <span style={{ color: "black", marginLeft: 8 }}>
                    {csvProductIds.length}
                  </span>
                </div>
              )}
            </Grid>

            {/* Category */}
            <Grid item xs={4}>
              <label className="title" style={{ marginLeft: 32 }}>
                Category:
              </label>
            </Grid>
            <Grid item xs={8}>
              <FormControl style={{ width: "100%" }} variant="outlined">
                <InputLabel id="category-label" style={{ color: "black" }}>
                  Category
                </InputLabel>
                <Select
                  labelId="category-label"
                  id="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  label="Category"
                  style={{ color: "black" }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400,
                        zIndex: 9999,
                        marginTop: 8
                      }
                    },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "right"
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "right"
                    },
                    getContentAnchorEl: null
                  }}
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  {allowedDeviceCategories.map((cat) => (
                    <MenuItem key={cat} value={cat} style={{ color: "black" }}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Firmware Version */}
            <Grid item xs={4}>
              <label className="title" style={{ marginLeft: 32 }}>
                Firmware Version:
              </label>
            </Grid>
            <Grid item xs={8}>
              <input
                type="text"
                value={fwVersion}
                onChange={(e) => setFwVersion(e.target.value)}
                required
                className="add-input xl"
                placeholder="Enter Firmware Version"
                style={{ width: "100%" }}
              />
              {fwVersionError && (
                <div style={{ color: "red", marginTop: 4 }}>
                  {fwVersionError}
                </div>
              )}
            </Grid>

            {/* CD File */}
            <Grid item xs={4}>
              <label className="title" style={{ marginLeft: 32 }}>
                Certificate (CD File):
              </label>
            </Grid>
            <Grid item xs={8}>
              <input
              ref={cdInputRef} 
                type="file"
                accept=".pem,.crt,.cer,.der,.p7b,.p7c,.key,.txt"
                onChange={(e) => setCdFile(e.target.files[0])}
                required
                style={{ color: "black" }}
              />
              {/* {cdFile && (
                <div style={{ color: "black", marginTop: 8 }}>
                  Selected file: {cdFile.name}
                </div>
              )} */}
            </Grid>

            {/* Buttons */}
            <Grid item xs={12} style={{ marginTop: 24 }}>
              <div className="row" style={{ display: "flex" }}>
                <button
                  className="btn btn-style-2 orange-bg"
                  type="submit"
                  disabled={loading}
                  style={{ marginLeft: "10px" }}
                >
                  {loading ? (
                    <Fragment>
                      <CircularProgress
                        size="2em"
                        style={{ color: "#AFB42B", marginTop: "5px" }}
                      />
                    </Fragment>
                  ) : (
                    "Upload CD"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-style-2"
                  style={{
                    marginLeft: "10px",
                    background: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={resetForm}
                  disabled={loading}
                >
                  Reset
                </button>
                {error && (
                  <div style={{ color: "red", marginLeft: 24 }}>{error}</div>
                )}
              </div>
            </Grid>
          </Grid>
        </form>
      </div>
    </div>
  );
}

export default UploadCDForm;
